import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { Layout } from './components/Layout.js';
import { RouteSeo } from './components/RouteSeo.js';
import { usePageViews, useOutboundTracking } from './lib/analytics.js';
import { useAuth, isAdmin } from './lib/auth.js';
// The landing page stays eager: it is the first paint for new visitors and
// must not wait on a second chunk. Everything else is route-split.
import LandingPage from './routes/landing/index.js';

const LoginPage = lazy(() => import('./pages/LoginPage.js'));
const TodayPage = lazy(() => import('./pages/TodayPage.js'));
const SaintsListPage = lazy(() => import('./pages/SaintsListPage.js'));
const SaintDetailPage = lazy(() => import('./pages/SaintDetailPage.js'));
const IconsPage = lazy(() => import('./pages/IconsPage.js'));
const FastingPage = lazy(() => import('./pages/FastingPage.js'));
const ReadingsPage = lazy(() => import('./pages/ReadingsPage.js'));
const ReadingPlansPage = lazy(() => import('./pages/ReadingPlansPage.js'));
const ServicesPage = lazy(() => import('./pages/ServicesPage.js'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage.js'));
const LearningPage = lazy(() => import('./pages/LearningPage.js'));
const CategoryArticlesPage = lazy(() => import('./pages/CategoryArticlesPage.js'));
const ArticleReaderPage = lazy(() => import('./pages/ArticleReaderPage.js'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage.js'));
const ResourceWorkPage = lazy(() => import('./pages/ResourceWorkPage.js'));
const ResourceBookPage = lazy(() => import('./pages/ResourceBookPage.js'));
const ResourceReaderPage = lazy(() => import('./pages/ResourceReaderPage.js'));
const JourneyPage = lazy(() => import('./pages/JourneyPage.js'));
const WatchPage = lazy(() => import('./pages/WatchPage.js'));
const PrayersPage = lazy(() => import('./pages/PrayersPage.js'));
const PrayerDetailPage = lazy(() => import('./pages/PrayerDetailPage.js'));
const JesusPrayerPage = lazy(() => import('./pages/JesusPrayerPage.js'));
const CalendarPage = lazy(() => import('./pages/CalendarPage.js'));
const FeastsListPage = lazy(() => import('./pages/FeastsListPage.js'));
const FeastDetailPage = lazy(() => import('./pages/FeastDetailPage.js'));
const ProfilePage = lazy(() => import('./pages/ProfilePage.js'));
const ParishPage = lazy(() => import('./pages/ParishPage.js'));
const ParishBulletinPage = lazy(() => import('./pages/ParishBulletinPage.js'));
const RegisterParishPage = lazy(() => import('./pages/RegisterParishPage.js'));
const TrackingPage = lazy(() => import('./pages/TrackingPage.js'));
const CommemorationsPage = lazy(() => import('./pages/CommemorationsPage.js'));
const ConfessionPage = lazy(() => import('./pages/ConfessionPage.js'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout.js'));
const AdminHomePage = lazy(() => import('./pages/admin/AdminHomePage.js'));
const BrandPage = lazy(() => import('./pages/admin/BrandPage.js'));
const ParishAdminPage = lazy(() => import('./pages/admin/ParishAdminPage.js'));
const ParishReviewPage = lazy(() => import('./pages/admin/ParishReviewPage.js'));
const QuestionsInboxPage = lazy(() => import('./pages/admin/QuestionsInboxPage.js'));
const HeroesAdminPage = lazy(() => import('./pages/admin/HeroesAdminPage.js'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js'));
const DevComponentsPage = lazy(() => import('./pages/dev/ComponentsPage.js'));

// Footer-linked content pages (PageLayout-wrapped).
const About = lazy(() => import('./pages/footer/About.js'));
const Support = lazy(() => import('./pages/footer/Support.js'));
const Sources = lazy(() => import('./pages/footer/Sources.js'));
const Contributors = lazy(() => import('./pages/footer/Contributors.js'));
const Press = lazy(() => import('./pages/footer/Press.js'));
const Brand = lazy(() => import('./pages/footer/Brand.js'));
const Status = lazy(() => import('./pages/footer/Status.js'));
const Changelog = lazy(() => import('./pages/footer/Changelog.js'));
const Roadmap = lazy(() => import('./pages/footer/Roadmap.js'));
const AppToday = lazy(() => import('./pages/footer/AppToday.js'));
const AppPrayers = lazy(() => import('./pages/footer/AppPrayers.js'));
const AppJesusPrayer = lazy(() => import('./pages/footer/AppJesusPrayer.js'));
const AppFasting = lazy(() => import('./pages/footer/AppFasting.js'));
const AppReadings = lazy(() => import('./pages/footer/AppReadings.js'));
const AppServices = lazy(() => import('./pages/footer/AppServices.js'));
const AppCalendar = lazy(() => import('./pages/footer/AppCalendar.js'));
const AppSaints = lazy(() => import('./pages/footer/AppSaints.js'));
const LearnCatechism = lazy(() => import('./pages/footer/LearnCatechism.js'));
const LearnLiturgy = lazy(() => import('./pages/footer/LearnLiturgy.js'));
const LearnFasting = lazy(() => import('./pages/footer/LearnFasting.js'));
const LearnSaints = lazy(() => import('./pages/footer/LearnSaints.js'));
const LearnSacraments = lazy(() => import('./pages/footer/LearnSacraments.js'));
const LearnChurchHistory = lazy(() => import('./pages/footer/LearnChurchHistory.js'));
const LearnInquirers = lazy(() => import('./pages/footer/LearnInquirers.js'));
const LearnCatechumens = lazy(() => import('./pages/footer/LearnCatechumens.js'));
const ParishFind = lazy(() => import('./pages/footer/ParishFind.js'));
const ParishAdmins = lazy(() => import('./pages/footer/ParishAdmins.js'));
const ParishAdd = lazy(() => import('./pages/footer/ParishAdd.js'));
const ParishServiceTimes = lazy(() => import('./pages/footer/ParishServiceTimes.js'));
const ParishEvents = lazy(() => import('./pages/footer/ParishEvents.js'));
const ParishAnnouncements = lazy(() => import('./pages/footer/ParishAnnouncements.js'));
const Privacy = lazy(() => import('./pages/footer/Privacy.js'));
const Terms = lazy(() => import('./pages/footer/Terms.js'));
const Cookies = lazy(() => import('./pages/footer/Cookies.js'));
const Accessibility = lazy(() => import('./pages/footer/Accessibility.js'));
const Contact = lazy(() => import('./pages/footer/Contact.js'));

function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const location = useLocation();
  if (!user)
    return <Navigate to={`/login?from=${encodeURIComponent(location.pathname)}`} replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login?from=/admin" replace />;
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Analytics() {
  usePageViews();
  useOutboundTracking();
  return null;
}

export default function App() {
  return (
    <>
      <RouteSeo />
      <Analytics />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register-parish" element={<RegisterParishPage />} />

          {import.meta.env.DEV && <Route path="/dev/components" element={<DevComponentsPage />} />}

          {/* Footer-linked content pages - Orthodox Way meta */}
          <Route path="/about" element={<About />} />
          <Route path="/support" element={<Support />} />
          <Route path="/sources" element={<Sources />} />
          <Route path="/contributors" element={<Contributors />} />
          <Route path="/press" element={<Press />} />
          <Route path="/brand" element={<Brand />} />
          <Route path="/status" element={<Status />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/roadmap" element={<Roadmap />} />

          {/* Footer-linked content pages - The App explainers */}
          <Route path="/app/today" element={<AppToday />} />
          <Route path="/app/prayers" element={<AppPrayers />} />
          <Route path="/app/jesus-prayer" element={<AppJesusPrayer />} />
          <Route path="/app/fasting" element={<AppFasting />} />
          <Route path="/app/readings" element={<AppReadings />} />
          <Route path="/app/services" element={<AppServices />} />
          <Route path="/app/calendar" element={<AppCalendar />} />
          <Route path="/app/saints" element={<AppSaints />} />

          {/* Footer-linked content pages - Learn */}
          <Route path="/learn/catechism" element={<LearnCatechism />} />
          <Route path="/learn/liturgy" element={<LearnLiturgy />} />
          <Route path="/learn/fasting" element={<LearnFasting />} />
          <Route path="/learn/saints" element={<LearnSaints />} />
          <Route path="/learn/sacraments" element={<LearnSacraments />} />
          <Route path="/learn/church-history" element={<LearnChurchHistory />} />
          <Route path="/learn/inquirers" element={<LearnInquirers />} />
          <Route path="/learn/catechumens" element={<LearnCatechumens />} />

          {/* Footer-linked content pages - Parish & Community */}
          <Route path="/parish/find" element={<ParishFind />} />
          <Route path="/parish/admins" element={<ParishAdmins />} />
          <Route path="/parish/add" element={<ParishAdd />} />
          <Route path="/parish/service-times" element={<ParishServiceTimes />} />
          <Route path="/parish/events" element={<ParishEvents />} />
          <Route path="/parish/announcements" element={<ParishAnnouncements />} />

          {/* Footer-linked content pages - Legal & Utility */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/contact" element={<Contact />} />

          <Route element={<Layout />}>
            <Route path="today" element={<TodayPage />} />
            <Route path="prayers" element={<PrayersPage />} />
            <Route path="prayers/jesus" element={<JesusPrayerPage />} />
            <Route path="prayers/:slug" element={<PrayerDetailPage />} />
            <Route path="fasting" element={<FastingPage />} />
            <Route path="readings" element={<ReadingsPage />} />
            <Route path="readings/:date" element={<ReadingsPage />} />
            <Route path="reading-plans" element={<ReadingPlansPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="services/:slug" element={<ServiceDetailPage />} />
            <Route path="learning" element={<LearningPage />} />
            <Route path="learn/category/:slug" element={<CategoryArticlesPage />} />
            <Route path="learn/articles/:slug" element={<ArticleReaderPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="resources/:work" element={<ResourceWorkPage />} />
            <Route path="resources/:work/:book" element={<ResourceBookPage />} />
            <Route path="resources/:work/:book/:chapter" element={<ResourceReaderPage />} />
            <Route path="journey" element={<JourneyPage />} />
            <Route path="watch" element={<WatchPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="feasts" element={<FeastsListPage />} />
            <Route path="feasts/:slug" element={<FeastDetailPage />} />
            <Route path="saints" element={<SaintsListPage />} />
            <Route path="saints/:slug" element={<SaintDetailPage />} />
            <Route path="icons" element={<IconsPage />} />
            <Route path="parish" element={<ParishPage />} />
            <Route path="parishes/:slug/bulletin" element={<ParishBulletinPage />} />
            <Route
              path="tracking"
              element={
                <RequireAuth>
                  <TrackingPage />
                </RequireAuth>
              }
            />
            <Route
              path="commemorations"
              element={
                <RequireAuth>
                  <CommemorationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="confession"
              element={
                <RequireAuth>
                  <ConfessionPage />
                </RequireAuth>
              }
            />
            <Route
              path="profile"
              element={
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<AdminHomePage />} />
              <Route path="brand" element={<BrandPage />} />
              <Route path="parish" element={<ParishAdminPage />} />
              <Route path="parish-review" element={<ParishReviewPage />} />
              <Route path="questions" element={<QuestionsInboxPage />} />
              <Route path="heroes" element={<HeroesAdminPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
